
import { useState, useEffect, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";

import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import AnimatedHeading from "../../../components/animations/AnimatedHeading.jsx";

import p1 from "../../../Assets/Images/p1.png";
import p2 from "../../../Assets/Images/p2.png";
import p3 from "../../../Assets/Images/p3.png";

import { useNavigate } from "react-router-dom";

/* ==========================================================================
   PROJECT DATA
   ========================================================================== */

const projects = [
  {
    src: p1,
    title: "Modern UI Design",
    category: "Design",
  },
  {
    src: p2,
    title: "Cloud Migration Success",
    category: "Solution",
  },
  {
    src: p3,
    title: "Mobile App Development",
    category: "App",
  },
  {
    src: p1,
    title: "E-Commerce Platform",
    category: "Web",
  },
  {
    src: p2,
    title: "AI Integration",
    category: "Technology",
  },
];

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export default function ExploreProjectsHero() {
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  /* ------------------------------------------------------------------------
     NEXT PROJECT
     ------------------------------------------------------------------------ */

  const nextProject = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
    setHoveredIndex(null);
  }, []);

  /* ------------------------------------------------------------------------
     PREVIOUS PROJECT
     ------------------------------------------------------------------------ */

  const previousProject = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === 0 ? projects.length - 1 : prev - 1
    );

    setHoveredIndex(null);
  }, []);

  /* ------------------------------------------------------------------------
     GO TO SPECIFIC PROJECT
     ------------------------------------------------------------------------ */

  const goToProject = (index) => {
    setCurrentIndex(index);
    setHoveredIndex(null);
  };

  /* ------------------------------------------------------------------------
     AUTOPLAY
     ------------------------------------------------------------------------ */

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      nextProject();
    }, 3500);

    return () => clearInterval(interval);
  }, [isPaused, nextProject]);

  /* ------------------------------------------------------------------------
     DESKTOP VISIBLE PROJECTS
     ------------------------------------------------------------------------ */

  const visibleProjects = [
    projects[currentIndex % projects.length],
    projects[(currentIndex + 1) % projects.length],
    projects[(currentIndex + 2) % projects.length],
  ];

  /* ------------------------------------------------------------------------
     DESKTOP HOVER GRID
     ------------------------------------------------------------------------ */

  const getGridConfig = () => {
    if (hoveredIndex === null) {
      return "1fr 1fr 1fr";
    }

    if (hoveredIndex === 0) {
      return "2.4fr 1fr 1fr";
    }

    if (hoveredIndex === 1) {
      return "1fr 2.4fr 1fr";
    }

    if (hoveredIndex === 2) {
      return "1fr 1fr 2.4fr";
    }

    return "1fr 1fr 1fr";
  };

  /* ------------------------------------------------------------------------
     PORTFOLIO NAVIGATION
     ------------------------------------------------------------------------ */

  const handlePortfolioNavigation = () => {
    navigate("/portfolio");

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, 50);
  };

  /* ==========================================================================
     RENDER
     ========================================================================== */

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        theme-bg-primary
        font-saira
        theme-text-primary
        px-4
        py-10
        sm:px-5
        sm:py-11
        md:px-7
        md:py-12
        lg:px-10
        lg:py-14
        xl:px-12
        xl:py-16
      "
    >
      {/* =====================================================================
          BACKGROUND GLOW
          ===================================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-20
          h-48
          w-48
          rounded-full
          bg-primaryColor/[0.045]
          blur-[100px]
          sm:h-60
          sm:w-60
          md:h-72
          md:w-72
          lg:h-80
          lg:w-80
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-10
          h-48
          w-48
          rounded-full
          bg-primaryColor/[0.035]
          blur-[100px]
          sm:h-60
          sm:w-60
          md:h-72
          md:w-72
          lg:h-80
          lg:w-80
        "
      />

      {/* =====================================================================
          MAIN CONTAINER
          ===================================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-[1500px]
        "
      >
        {/* ===================================================================
            HEADER
            =================================================================== */}

        <header
          className="
            mx-auto
            mb-8
            w-full
            max-w-4xl
            px-1
            text-center
            sm:mb-9
            md:mb-10
            lg:mb-12
          "
        >
          {/* EYEBROW */}

          <p
            className="
              brand-color
              text-[9px]
              font-bold
              uppercase
              tracking-[0.22em]
              sm:text-[10px]
              sm:tracking-[0.28em]
              md:text-xs
              md:tracking-[0.35em]
            "
          >
            [ Explore Projects ]
          </p>

          {/* HEADING */}

          <AnimatedHeading
            as="h1"
            parts={[
              "Transforming Ideas Into",
              "Real Digital Success.",
            ]}
            className="
              mx-auto
              mt-3
              max-w-[94vw]
              text-2xl
              font-semibold
              leading-tight
              tracking-tight
              theme-text-primary
              sm:mt-4
              sm:max-w-2xl
              sm:text-3xl
              md:text-4xl
              lg:mt-5
              lg:max-w-4xl
              lg:text-5xl
              xl:text-[52px]
            "
          />
        </header>

        {/* ===================================================================
            MOBILE CAROUSEL
            Visible below md
            =================================================================== */}

        <div
          className="
            relative
            block
            md:hidden
          "
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          {/* MOBILE CARD */}

          <div className="relative mx-auto w-full max-w-[520px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{
                  opacity: 0,
                  x: 70,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                exit={{
                  opacity: 0,
                  x: -70,
                }}
                transition={{
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                drag="x"
                dragConstraints={{
                  left: 0,
                  right: 0,
                }}
                dragElastic={0.15}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -60) {
                    nextProject();
                  }

                  if (info.offset.x > 60) {
                    previousProject();
                  }
                }}
                className="
                  relative
                  h-[390px]
                  w-full
                  overflow-hidden
                  rounded-2xl
                  border
                  theme-border
                  theme-card
                  shadow-2xl
                  xs:h-[410px]
                  sm:h-[450px]
                "
              >
                {/* IMAGE */}

                <motion.img
                  src={projects[currentIndex].src}
                  alt={projects[currentIndex].title}
                  draggable={false}
                  className="
                    absolute
                    inset-0
                    h-full
                    w-full
                    select-none
                    object-cover
                  "
                  initial={{ scale: 1.05 }}
                  animate={{ scale: 1 }}
                  transition={{
                    duration: 0.8,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />

                {/* THEME-AWARE OVERLAY */}

                <div
                  className="
                    absolute
                    inset-0
                    bg-black/20
                    dark:bg-black/20
                  "
                />

                {/* BOTTOM GRADIENT */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-x-0
                    bottom-0
                    h-[60%]
                    bg-gradient-to-t
                    from-black/90
                    via-black/50
                    to-transparent
                  "
                />

                {/* PROJECT CARD */}

                <div
                  className="
                    absolute
                    bottom-4
                    left-4
                    right-4
                    flex
                    items-center
                    justify-between
                    gap-3
                    rounded-xl
                    border
                    theme-border
                    bg-black/75
                    p-3.5
                    backdrop-blur-xl
                    sm:bottom-5
                    sm:left-5
                    sm:right-5
                    sm:p-4
                  "
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className="
                        brand-color
                        text-[9px]
                        font-medium
                        uppercase
                        tracking-[0.16em]
                        sm:text-[10px]
                      "
                    >
                      {projects[currentIndex].category}
                    </p>

                    <p
                      className="
                        mt-1
                        truncate
                        text-sm
                        font-semibold
                        text-white
                        sm:text-base
                      "
                    >
                      {projects[currentIndex].title}
                    </p>
                  </div>

                  <div
                    className="
                      brand-bg
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      text-black
                      sm:h-10
                      sm:w-10
                    "
                  >
                    <ArrowUpRight
                      className="h-[18px] w-[18px]"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>

                {/* MOBILE SWIPE HINT */}

                <div
                  className="
                    absolute
                    left-1/2
                    top-4
                    -translate-x-1/2
                    rounded-full
                    border
                    theme-border
                    bg-black/45
                    px-3
                    py-1
                    text-[8px]
                    uppercase
                    tracking-[0.16em]
                    text-white/60
                    backdrop-blur-md
                  "
                >
                  Swipe
                </div>
              </motion.div>
            </AnimatePresence>

            {/* MOBILE PREVIOUS BUTTON */}

            <button
              type="button"
              onClick={previousProject}
              aria-label="Previous project"
              className="
                absolute
                left-2
                top-1/2
                z-20
                flex
                h-10
                w-10
                -translate-y-1/2
                items-center
                justify-center
                rounded-full
                border
                theme-border
                bg-black/70
                text-white
                shadow-lg
                backdrop-blur-md
                transition-all
                duration-300
                active:scale-90
                sm:left-3
                sm:h-11
                sm:w-11
              "
            >
              <ChevronLeft
                className="h-5 w-5"
                strokeWidth={2}
              />
            </button>

            {/* MOBILE NEXT BUTTON */}

            <button
              type="button"
              onClick={nextProject}
              aria-label="Next project"
              className="
                absolute
                right-2
                top-1/2
                z-20
                flex
                h-10
                w-10
                -translate-y-1/2
                items-center
                justify-center
                rounded-full
                brand-bg
                text-black
                shadow-lg
                transition-all
                duration-300
                active:scale-90
                sm:right-3
                sm:h-11
                sm:w-11
              "
            >
              <ChevronRight
                className="h-5 w-5"
                strokeWidth={2.5}
              />
            </button>
          </div>
        </div>

        {/* ===================================================================
            TABLET / DESKTOP CAROUSEL
            Visible md+
            =================================================================== */}

        <div
          className="
            relative
            hidden
            md:block
          "
        >
          <motion.div
            animate={{
              gridTemplateColumns: getGridConfig(),
            }}
            transition={{
              type: "spring",
              stiffness: 150,
              damping: 20,
            }}
            className="
              mx-auto
              grid
              w-full
              grid-cols-3
              gap-4
              md:h-[360px]
              lg:h-[430px]
              lg:gap-5
              xl:h-[500px]
            "
          >
            <AnimatePresence mode="popLayout">
              {visibleProjects.map((project, i) => (
                <motion.div
                  key={`${project.title}-${currentIndex}-${i}`}
                  layout
                  initial={{
                    opacity: 0,
                    x: 50,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: -50,
                  }}
                  transition={{
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  onMouseEnter={() => {
                    setHoveredIndex(i);
                    setIsPaused(true);
                  }}
                  onMouseLeave={() => {
                    setHoveredIndex(null);
                    setIsPaused(false);
                  }}
                  className="
                    group
                    relative
                    min-w-0
                    cursor-pointer
                    overflow-hidden
                    rounded-2xl
                    border
                    theme-border
                    theme-card
                  "
                >
                  {/* IMAGE */}

                  <motion.img
                    src={project.src}
                    alt={project.title}
                    draggable={false}
                    className="
                      absolute
                      inset-0
                      h-full
                      w-full
                      select-none
                      object-cover
                    "
                    whileHover={{
                      scale: 1.06,
                    }}
                    transition={{
                      duration: 0.55,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  />

                  {/* IMAGE OVERLAY */}

                  <div
                    className="
                      absolute
                      inset-0
                      bg-black/25
                      transition-all
                      duration-500
                      group-hover:bg-black/10
                    "
                  />

                  {/* GRADIENT */}

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-x-0
                      bottom-0
                      h-[55%]
                      bg-gradient-to-t
                      from-black/80
                      via-black/20
                      to-transparent
                    "
                  />

                  {/* PROJECT INFORMATION */}

                  <motion.div
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    animate={{
                      opacity:
                        hoveredIndex === i ||
                        (hoveredIndex === null && i === 1)
                          ? 1
                          : 0,
                      y:
                        hoveredIndex === i ||
                        (hoveredIndex === null && i === 1)
                          ? 0
                          : 20,
                    }}
                    transition={{
                      duration: 0.35,
                    }}
                    className="
                      absolute
                      bottom-4
                      left-4
                      right-4
                      flex
                      items-center
                      justify-between
                      gap-3
                      rounded-xl
                      border
                      theme-border
                      bg-black/75
                      p-3.5
                      backdrop-blur-xl
                      lg:bottom-5
                      lg:left-5
                      lg:right-5
                      lg:p-4
                    "
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className="
                          brand-color
                          text-[9px]
                          font-medium
                          uppercase
                          tracking-[0.14em]
                          lg:text-[10px]
                        "
                      >
                        {project.category}
                      </p>

                      <p
                        className="
                          mt-1
                          truncate
                          text-sm
                          font-semibold
                          text-white
                          lg:text-base
                        "
                      >
                        {project.title}
                      </p>
                    </div>

                    <div
                      className="
                        brand-bg
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        text-black
                        lg:h-9
                        lg:w-9
                      "
                    >
                      <ArrowUpRight
                        className="h-4 w-4"
                        strokeWidth={2.5}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* DESKTOP PREVIOUS BUTTON */}

          <button
            type="button"
            onClick={previousProject}
            aria-label="Previous project"
            className="
              absolute
              -left-5
              top-1/2
              z-30
              flex
              h-11
              w-11
              -translate-y-1/2
              items-center
              justify-center
              rounded-full
              border
              theme-border
              bg-black/80
              text-white
              shadow-xl
              backdrop-blur-md
              transition-all
              duration-300
              hover:scale-110
              hover:border-primaryColor
              hover:text-primaryColor
              active:scale-95
              lg:-left-6
            "
          >
            <ChevronLeft
              className="h-5 w-5"
              strokeWidth={2}
            />
          </button>

          {/* DESKTOP NEXT BUTTON */}

          <button
            type="button"
            onClick={nextProject}
            aria-label="Next project"
            className="
              absolute
              -right-5
              top-1/2
              z-30
              flex
              h-11
              w-11
              -translate-y-1/2
              items-center
              justify-center
              rounded-full
              brand-bg
              text-black
              shadow-xl
              transition-all
              duration-300
              hover:scale-110
              active:scale-95
              lg:-right-6
            "
          >
            <ChevronRight
              className="h-5 w-5"
              strokeWidth={2.5}
            />
          </button>
        </div>

        {/* ===================================================================
            CONTROLS
            =================================================================== */}

        <div
          className="
            mt-8
            flex
            flex-col
            items-center
            gap-6
            sm:mt-9
            md:mt-10
            lg:mt-12
            lg:gap-7
          "
        >
          {/* DOTS */}

          <div
            className="
              flex
              items-center
              justify-center
              gap-1.5
              sm:gap-2
            "
          >
            {projects.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToProject(index)}
                aria-label={`Go to project ${index + 1}`}
                aria-current={
                  currentIndex === index ? "true" : undefined
                }
                className={`
                  h-1.5
                  rounded-full
                  transition-all
                  duration-300
                  focus:outline-none

                  ${
                    currentIndex === index
                      ? "w-7 brand-bg sm:w-8"
                      : "w-1.5 bg-zinc-700 hover:bg-zinc-500 sm:w-2"
                  }
                `}
              />
            ))}
          </div>

          {/* EXPLORE MORE */}

          <button
            type="button"
            onClick={handlePortfolioNavigation}
            className="
              group
              relative
              inline-flex
              items-center
              justify-center
              overflow-hidden
              rounded-full
              brand-bg
              px-4
              py-2
              font-semibold
              text-black
              shadow-md
              transition-all
              duration-300
              hover:scale-[1.03]
              hover:text-white
              hover:shadow-xl
              active:scale-95
              sm:px-5
              sm:py-2.5
              md:px-6
              md:py-3
            "
          >
            {/* HOVER BACKGROUND */}

            <span
              aria-hidden="true"
              className="
                absolute
                inset-0
                w-0
                bg-black
                transition-all
                duration-500
                ease-out
                group-hover:w-full
              "
            />

            {/* CONTENT */}

            <span
              className="
                relative
                z-10
                flex
                items-center
                gap-2
                text-xs
                sm:text-sm
                sm:gap-2.5
                md:text-base
                md:gap-3
              "
            >
              Explore More

              <span
                className="
                  inline-flex
                  h-7
                  w-7
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-black
                  text-white
                  transition-all
                  duration-500
                  group-hover:translate-x-1
                  group-hover:rotate-45
                  group-hover:brand-bg
                  group-hover:text-black
                  sm:h-8
                  sm:w-8
                  md:h-9
                  md:w-9
                "
              >
                <ArrowUpRight
                  className="
                    h-4
                    w-4
                    sm:h-[18px]
                    sm:w-[18px]
                    md:h-5
                    md:w-5
                  "
                  strokeWidth={2.3}
                />
              </span>
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}

